import React, { useCallback, useEffect, useState } from 'react'
import { Button, Table } from 'react-bootstrap'
import { ArrowLeft } from 'react-bootstrap-icons'
import { useNavigate } from 'react-router'
import { useSearchParams } from 'react-router-dom'
import { ToastContainer } from 'react-toastify'

import Badge from '../../components/_atoms/Badge/Badge'
import ListNoItems from '../../components/_atoms/ListNoItems/ListNoItems'
import ListPagination from '../../components/_atoms/ListPagination/ListPagination'
import ListSkeleton from '../../components/_atoms/ListSkeleton/ListSkeleton'
import SalesText from '../../components/_atoms/SalesText/SalesText'
import SearchComponentRTK from '../../components/_molecules/SearchComponent/SearchComponentRTK'
import { fontSizes, fontWeights } from '../../constants/salesStyles'
import { ToastType, useToast } from '../../hooks/useToast'
import { useGetClientsHistoryListQuery } from '../../modules/store/dochubApi/dochubApi'
import { ClientHistoryChange, ClientsHistorySearchParams } from '../../modules/store/dochubApi/dochubTypes'
import { adminRouteAlias, getAdminNavigationPath } from '../../router/adminRouteAlias'
import { formatDate } from '../../utils/converters'
import { colWidth } from '../../utils/stylePatches'

import styles from './styles.module.scss'

const CHANGE_MSG_LABELS: Record<string, string> = {
    'client created': 'Клиент создан',
    'name changed': 'Имя изменено',
    'phone changed': 'Телефон изменён',
    'email changed': 'Email изменён',
    'public comment changed': 'Примечание изменено',
    'private note changed': 'Заметка изменена',
    'client deleted': 'Клиент удалён',
}

const formatChangeLine = (change: ClientHistoryChange) => {
    const label = CHANGE_MSG_LABELS[change.msg] || change.msg

    if (change.msg === 'client created') {
        return `${label}: ${change.new}`
    }

    if (change.msg === 'client deleted') {
        return label
    }

    if (!change.old && change.new) {
        return `${label}: ${change.new}`
    }

    if (change.old && !change.new) {
        return `${label}: ${change.old} → —`
    }

    return `${label}: ${change.old} → ${change.new}`
}

const ClientHistoryList = () => {
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const searchPage = Number(searchParams.get('page')) || 1
    const searchQuery = searchParams.get('search') || ''

    const [filters, setFilters] = useState<ClientsHistorySearchParams>({
        pagination: {
            page: searchPage,
            perPage: 50,
        },
        search: searchQuery,
    })

    const {
        pagination: { page, perPage },
        search,
    } = filters

    const {
        data,
        isFetching: isLoading,
        isError,
    } = useGetClientsHistoryListQuery(
        {
            params: {
                pagination: { page, perPage },
                search,
            },
        },
        {
            refetchOnMountOrArgChange: true,
        },
    )

    const historyList = data?.data || []
    const historyCount = data?.dataCount || 0

    const setFiltersAndUpdateUrl = (newFilters: ClientsHistorySearchParams) => {
        setFilters(newFilters)

        const newParams = new URLSearchParams()

        if (newFilters.pagination.page > 1) {
            newParams.set('page', String(newFilters.pagination.page))
        }
        if (newFilters.search) {
            newParams.set('search', newFilters.search)
        }

        navigate(
            {
                pathname: getAdminNavigationPath(adminRouteAlias.clients.history),
                search: newParams.toString(),
            },
            { replace: false },
        )
    }

    const setPage = (newPage: number) => {
        setFiltersAndUpdateUrl({
            ...filters,
            pagination: { ...filters.pagination, page: newPage },
        })
    }

    const setSearchTerm = (newTerm: string) => {
        setFiltersAndUpdateUrl({
            ...filters,
            search: newTerm,
            pagination: { ...filters.pagination, page: 1 },
        })
    }

    const renderListPagination = useCallback(() => {
        if (!isLoading && historyCount === 0) return null

        return (
            <ListPagination
                page={page}
                setPage={setPage}
                perPage={perPage}
                itemsCount={historyCount}
                justifyContent="flex-end"
            />
        )
    }, [historyCount, isLoading, page, perPage])

    useEffect(() => {
        setFilters(prev => ({
            ...prev,
            pagination: {
                ...prev.pagination,
                page: searchPage,
            },
            search: searchQuery,
        }))
    }, [searchPage, searchQuery])

    useToast({ isTriggered: isError, type: ToastType.ERROR, message: 'Ошибка загрузки истории клиентов' })

    return (
        <div className={styles.wrap}>
            <ToastContainer position="bottom-center" autoClose={5000} />

            <div className={styles.rowWrap}>
                <Button
                    variant="link"
                    className={styles.backButton}
                    onClick={() => navigate(getAdminNavigationPath(adminRouteAlias.clients.location))}
                >
                    <ArrowLeft size={18} />
                    К клиентам
                </Button>
                <SalesText
                    text={'История изменений клиентов'}
                    fontWeight={fontWeights.bold}
                    fontSize={fontSizes.xxl}
                    letterSpacing={-0.02}
                />
            </div>

            <SearchComponentRTK
                searchTerm={filters.search}
                setSearchTerm={setSearchTerm}
                placeholder="Поиск по клиентам"
            />

            {renderListPagination()}

            <Table striped hover>
                <thead>
                    <tr>
                        <th scope="col" style={colWidth(120)}>
                            Дата
                        </th>
                        <th scope="col" style={colWidth(200)}>
                            Клиент
                        </th>
                        <th scope="col">Изменения</th>
                    </tr>
                </thead>
                {isLoading ? (
                    <ListSkeleton rowCount={9} columnCount={3} marginVerticalRem={0.21} />
                ) : (
                    <tbody>
                        {historyList.map(item => (
                            <tr key={item.id}>
                                <td>
                                    <Badge text={formatDate(item.createdAt)} color="#ddd" fontColor="#555" />
                                </td>
                                <td>
                                    {item.clientCode} {item.clientName}
                                </td>
                                <td>
                                    <ul className={styles.changesList}>
                                        {item.changes.map((change, index) => (
                                            <li key={`${item.id}-${index}`}>{formatChangeLine(change)}</li>
                                        ))}
                                    </ul>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                )}
            </Table>

            {!isLoading && historyCount === 0 ? (
                <ListNoItems
                    resetFilters={() => navigate(getAdminNavigationPath(adminRouteAlias.clients.history))}
                />
            ) : (
                renderListPagination()
            )}
        </div>
    )
}

export default ClientHistoryList
